import { WellImageScoreDTO } from 'business/models/Models';
import React, { createContext, useContext, useState, ComponentType } from 'react';
import { GetScoreDefn } from 'utils/Global';

export type ImageScoringSortState = {
  imagesList: WellImageScoreDTO[];
  isFetchingData: boolean;
  selectedImageIndex: number;
  searchedAlready: boolean;
};

export interface ImageScoringSortContextType {
  state: ImageScoringSortState;
  updateState: (newState: ImageScoringSortState) => void;
  setStateDirectly: React.Dispatch<React.SetStateAction<ImageScoringSortState>>;
}

const ImageScoringSortContext = createContext<ImageScoringSortContextType | undefined>(
  undefined,
);
export const withImageScoringSortContext = <P extends object>(
  Component: ComponentType<P>,
  initialState: ImageScoringSortState,
) => {
  return (props: P) => {
    const [state, setState] = useState<ImageScoringSortState>(initialState);

    const updateState = (newState: ImageScoringSortState) => {
      setState(newState);
    };

    return (
      <ImageScoringSortContext.Provider
        value={{ state, updateState, setStateDirectly: setState }}
      >
        <Component {...props} />
      </ImageScoringSortContext.Provider>
    );
  };
};

export const useImageScoringSortContext = (): ImageScoringSortContextType => {
  const context = useContext(ImageScoringSortContext);
  if (!context) {
    throw new Error('ImageScoringSortContext must be used within a MyContext provider');
  }
  return context;
};

export const useIsFetchingData = () => {
  const { state } = useImageScoringSortContext();
  return state.isFetchingData;
};

export const useSearchedAlready = () => {
  const { state } = useImageScoringSortContext();
  return state.searchedAlready;
};

export const useImagesList = () => {
  const { state } = useImageScoringSortContext();
  return state.imagesList;
};

export const useSetIsFetchingData = () => {
  const { updateState, state } = useImageScoringSortContext();
  return (isFetchingData: boolean) => {
    updateState({ ...state, isFetchingData });
  };
};

export const useSelectedImageIndex = () => {
  const { state } = useImageScoringSortContext();
  return state.selectedImageIndex;
};

export const useSelectedImageNumber = () => {
  const { state } = useImageScoringSortContext();
  return state.selectedImageIndex + 1;
};

export const useSelectedImage = () => {
  const { state } = useImageScoringSortContext();
  return state.imagesList[state.selectedImageIndex];
};

export const useSetSelectedImageIndex = () => {
  const { updateState, state } = useImageScoringSortContext();
  return (index: number) => {
    updateState({ ...state, selectedImageIndex: index });
  };
};

export const useSetimagesList = () => {
  const { updateState, state } = useImageScoringSortContext();
  return (imagesList: WellImageScoreDTO[], fetchingStatusToSet: boolean) => {
    updateState({
      ...state,
      imagesList,
      isFetchingData: fetchingStatusToSet,
      selectedImageIndex: 0,
      searchedAlready: true,
    });
  };
};

export const useSetInitialState = () => {
  const { updateState, state } = useImageScoringSortContext();
  return (imagesList: WellImageScoreDTO[], shouldSetFetchingStatusFalse: boolean) => {
    shouldSetFetchingStatusFalse
      ? updateState({ ...state, imagesList, isFetchingData: false })
      : updateState({ ...state, imagesList });
  };
};

export const useMoveSelectedImageIndex = () => {
  const { setStateDirectly } = useImageScoringSortContext();
  return (backForward: number) => {
    setStateDirectly((prvState) => ({
      ...prvState,
      selectedImageIndex: _handleIndexMoveManipulation(prvState, backForward),
    }));
  };
};

const _handleIndexMoveManipulation = (
  prvState: ImageScoringSortState,
  numberToAdd: number,
) => {
  if (
    prvState.selectedImageIndex + numberToAdd < 0 ||
    prvState.selectedImageIndex + numberToAdd > prvState.imagesList.length - 1
  ) {
    return prvState.selectedImageIndex;
  }

  return prvState.selectedImageIndex + numberToAdd;
};

const typeIds = {};
export const getWellDropScoreType = (wellDropScoreId: number) => {
  if (!GetScoreDefn()) {
    return;
  }

  if (typeIds[wellDropScoreId]) {
    return typeIds[wellDropScoreId];
  }

  const score = GetScoreDefn().find(
    (_score) => _score.wellDropScoreId === wellDropScoreId,
  );

  typeIds[wellDropScoreId] = score.autoScoreType;

  return typeIds[wellDropScoreId];
};
